// Background self-heal: every dashboard load makes sure the tenant's
// hostname(s) are actually registered on the lyfty-storefront Cloudflare
// Pages project. The flow:
//
//   1. Read public.list_pending_domain_syncs() — any sync that the
//      sync-domain function recorded as 'pending' (not yet 'active').
//      That covers the "user clicked save, function got 502, pending row
//      stayed behind" case the platform used to leak.
//
//   2. Always re-fire sync for the current tenant's `subdomain`. The
//      Cloudflare API treats "already exists" as success, so this is
//      a free no-op when everything is healthy. When CF's domains list
//      lost the row (rare but happens), this is the only repair path.
//
// This hook never throws and never surfaces UI — it's hygiene. Failures
// just retry on the next session.
//
// Pre-requisites:
//   - useDataService provides the dashboard's authenticated client.
//   - useCurrentTenant resolves the tenant_id + subdomain.
//   - sync-domain function is idempotent (Sprint 2/4 guarantee).

import { useEffect, useRef } from 'react';
import { useCurrentTenant } from './use-tenant';
import { useAuthService, useDataService } from '@/lib/service-context';

const SESSION_KEY_PREFIX = 'lyfty-cf-pages-synced-tenant-';

export function useEnsureSubdomainBound(): void {
  const tenantQuery = useCurrentTenant();
  const authService = useAuthService();
  const dataService = useDataService();
  const subdomain = tenantQuery.data?.subdomain ?? null;
  const tenantId = tenantQuery.data?.id ?? null;
  const customDomain = tenantQuery.data?.customDomain ?? null;
  const ranThisSessionRef = useRef(false);

  useEffect(() => {
    if (!tenantId || !subdomain) return;
    if (typeof window === 'undefined') return;
    if (ranThisSessionRef.current) return;
    // Per-session guard via sessionStorage (survives strict-mode
    // double-invokes; resets on full tab refresh).
    const key = `${SESSION_KEY_PREFIX}${tenantId}`;
    if (window.sessionStorage.getItem(key) === '1') {
      ranThisSessionRef.current = true;
      return;
    }
    window.sessionStorage.setItem(key, '1');
    ranThisSessionRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const accessToken = await authService.getAccessToken();
        if (!accessToken || cancelled) return;

        // 1. Replay any pending sync jobs (max 5 per session — anything
        //    beyond that is broken at a deeper layer than self-heal).
        const pending = await dataService.domainSync.listPending().catch(() => []);
        const replays = pending.slice(0, 5).map((p) => p.hostname);

        // 2. Always include the current subdomain + custom domain.
        //    These are the canonical hosts; firing again is idempotent.
        const expectedHosts = new Set<string>(replays);
        const root = (import.meta.env.VITE_STOREFRONT_ROOT_DOMAIN as string | undefined) ?? 'gesty.tn';
        if (subdomain) expectedHosts.add(`${subdomain}.${root}`);
        if (customDomain) expectedHosts.add(customDomain);

        for (const host of expectedHosts) {
          if (cancelled) return;
          // Use the full-host body so the function doesn't try to prepend
          // root for already-resolved hosts.
          await fetch('/api/storefront/sync-domain', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
              'X-Supabase-URL': import.meta.env.VITE_SUPABASE_URL,
              'X-Supabase-AnonKey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({ newHost: host, oldHost: null }),
          }).catch(() => undefined);
        }
      } catch {
        // Silent — next session will retry.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantId, subdomain, customDomain, authService, dataService]);
}
